import React, { useEffect, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    Paper,
    Avatar,
    AvatarGroup
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SubjectIcon from '@mui/icons-material/Subject';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import MessageIcon from '@mui/icons-material/Message';

import { useTranslation } from "react-i18next";

import { getTextColor } from '../../utils/ColorUtils.jsx'


import '../../css/components/Statistics/ViewStatisticalTasks.css';
import TinyCustomFallback from 'components/Custom/TinyCustomFallback.jsx';
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip.jsx';


const TaskCard = ({ task, workspaceWorkers, myUserTimezone, taskLabels }) => {
    const { t } = useTranslation();


    const getColorForTask = (task) => {
        if (!myUserTimezone) {
            return "transparent";
        }
        const startDate = task.start_date
        const dueDate = task.due_date
        const endDate = task.end_date

        if (dueDate && endDate && new Date(endDate) > new Date(dueDate)) {
            const timeDifference = new Date(endDate) - new Date(dueDate);

            const overdueDays = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
            const key = overdueDays === 1 ? 'project_page.due_completed_singular' : 'project_page.due_completed_plural';

            return {
                backgroundColor: "#5d1f1a",
                color: "#fd9891",
                tooltip: t(key, { count: overdueDays })
            };
        }
        if (endDate) {
            return { backgroundColor: "#4bce97", color: "#1D2125", tooltip: t('project_page.completed_task', { date: formatDate(endDate) }) };
        }
        if (dueDate) {
            return { backgroundColor: "#f5cd47", color: "#1D2125", tooltip: t('project_page.due_by', { date: formatDate(dueDate) }) };
        }
        if (startDate) {
            return { backgroundColor: "#1a325d", color: "#91c6fd", tooltip: t('project_page.start_task', { date: formatDate(startDate) }) };
        }
        return { backgroundColor: "transparent", color: "#000000", tooltip: "" };
    };


    const formatDate = (date) => {
        const monthNames = [
            t('project_page.months.jan'),
            t('project_page.months.feb'),
            t('project_page.months.mar'),
            t('project_page.months.apr'),
            t('project_page.months.may'),
            t('project_page.months.jun'),
            t('project_page.months.jul'),
            t('project_page.months.aug'),
            t('project_page.months.sep'),
            t('project_page.months.oct'),
            t('project_page.months.nov'),
            t('project_page.months.dec')
        ];


        const newDate = new Date(date);
        const day = newDate.getDate();
        const month = monthNames[newDate.getMonth()];

        return `${day} ${month}`;
    };

    return (<>
        {task.cover_image || task.cover_color ? (

            <Paper
                className={`list-cover-paper-project-page ${task.cover_image ? 'image' : ''}`}
                sx={{
                    backgroundColor: task.cover_color,
                    backgroundImage: task.cover_image ? `url(${task.cover_image})` : 'none',
                }}
            />
        ) : null
        }
        < Paper
            elevation={4}
            className={`task-paper-project-page dragging-task-paper-project-page ${task.cover_image || task.cover_color ? 'cover' : ''}`}
        >
            <div className="flex-project-page">
                {task?.label?.map((labelId, index) => {
                    const fullLabel = taskLabels.find(label => label.id === labelId);
                    if (!fullLabel) return null;

                    const textColor = getTextColor(fullLabel.color);

                    return (
                        <div
                            key={`label-${labelId}`}
                            className={`label-style-project-page
                            ${fullLabel.color === 'rainbow' ? 'rainbow' : ''}`}
                            style={{ backgroundColor: fullLabel.color }}
                        >
                            <Typography
                                className={`label-title-project-page
                                ${fullLabel.color === 'rainbow' ? 'no-shadow' : 'with-shadow'}`}
                                style={{ color: textColor }}
                            >
                                {fullLabel.title}
                            </Typography>
                        </div>
                    );
                })}
            </div>



            <div className="task-title-project-page">
                <span>
                    <b>#{task.frontend_order}</b> - {task.title}
                </span>
            </div>


            <div className="dragging-task-dates-project-page">
                {(task.start_date || task.due_date || task.end_date) && (
                    <ScrollAwareTooltip title={getColorForTask(task).tooltip} arrow>
                        <div className="dragging-task-tooltip-project-page">
                            <div
                                className="dragging-task-tooltip-content-project-page"
                                style={{
                                    backgroundColor: getColorForTask(task).backgroundColor,
                                    color: getColorForTask(task).color
                                }}>
                                <AccessTimeIcon className="access-time-icon-project-page" />
                                {task.end_date ? `${formatDate(task.end_date)}\u00A0` :
                                    task.due_date ? `${formatDate(task.due_date)}\u00A0` :
                                        task.start_date ? `${formatDate(task.start_date)}\u00A0` :
                                            '\u00A0'}
                            </div>
                        </div>
                    </ScrollAwareTooltip>
                )}
                {task.has_description && (
                    <div>
                        <ScrollAwareTooltip title={t('project_page.task_desc')} arrow>
                            <SubjectIcon />
                        </ScrollAwareTooltip>
                    </div>
                )}
                {task.has_attachments && (
                    <ScrollAwareTooltip title={t('project_page.task_files')} arrow>
                        <AttachFileIcon />
                    </ScrollAwareTooltip>
                )}
                {task.has_comments && (
                    <ScrollAwareTooltip title={t('project_page.comments')} arrow>
                        <div className="checkbox-div-project-page">
                            <MessageIcon className="message-icon-project-page" />
                        </div>
                    </ScrollAwareTooltip>
                )}

                {task.checklist_completion && task.checklist_completion.total > 0 && (
                    <ScrollAwareTooltip title={t('project_page.checklist')} arrow>
                        <div className="checkbox-div-project-page">
                            <CheckBoxIcon className="checkbox-icon-project-page" /> {/* Dark-ish blue color */}
                            <Typography variant="body2" className="checkbox-text-project-page">
                                {`${task.checklist_completion.completed}/${task.checklist_completion.total}`}
                            </Typography>
                        </div>
                    </ScrollAwareTooltip>
                )}
                <div className="members-avatar-div-project-page">
                    <AvatarGroup
                        max={Math.max(2, 4 - (task.has_description ? 1 : 0)
                            - (task.has_comments ? 1 : 0)
                            - ((task.checklist_completion && task.checklist_completion.total > 0) ? 1 : 0))}

                        className="members-avatar-group-project-page"
                    >
                        {task.members?.map((memberId, index) => {
                            const fullMember = workspaceWorkers?.find(worker => worker.id === memberId);

                            if (fullMember) {
                                return (
                                    <ScrollAwareTooltip key={`member-${index}-${memberId}`} title={fullMember.full_name || fullMember.username} arrow >
                                        <Avatar
                                            className="members-avatar-project-page"
                                            src={`${fullMember.image}`}
                                        />
                                    </ScrollAwareTooltip>
                                );
                            }
                            return null;
                        })}
                    </AvatarGroup>
                </div>

            </div>
        </Paper>
    </>
    );

};

const TaskStatisticsModal = ({
    open,
    handleClose,
    statType,
    workspaceId,
    userId,
    selectedProjectId,
    WorkspaceStatsTaskView,
    workspaceWorkers,
    myUserTimezone,
    startDate,
    endDate,
    t
}) => {
    const [tasks, setTasks] = useState([]);

    const [taskLabels, setTaskLabels] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open) {
            const fetchTasks = async () => {
                setLoading(true);
                try {
                    const params = { stat_type: statType };
                    if (userId) {
                        params.user_id = userId;
                    }
                    if (selectedProjectId) {
                        params.project_id = selectedProjectId;
                    }
                    if (startDate) params.start_date = startDate;
                    if (endDate) params.end_date = endDate;

                    const data = await WorkspaceStatsTaskView(workspaceId, params);
                    const tasksArray = Array.isArray(data) ? data : data.tasks || [];
                    setTasks(tasksArray);
                    setTaskLabels(data.labels);
                } catch (error) {
                    console.error('Error fetching tasks', error);
                }
                setLoading(false);
            };
            fetchTasks();
        }
    }, [open, statType, workspaceId, userId, selectedProjectId, startDate, endDate]);


    const groupedTasks = tasks.reduce((acc, task) => {
        const project = task.project_name || 'Unknown Project';
        if (!acc[project]) {
            acc[project] = [];
        }
        acc[project].push(task);
        return acc;
    }, {});

    const taskGroups = Object.keys(groupedTasks).map(projectName => ({
        projectName,
        tasks: groupedTasks[projectName]
    }));

    return (
        <Modal open={open} onClose={handleClose} t={t} aria-labelledby="task-stats-modal-title" aria-describedby="task-stats-modal-description">
            <Box className="modal-container">
                <Typography id="task-stats-modal-title" variant="h6" sx={{ mb: 2 }}>
                    {statType === 'completed'
                        ? t("statistics.tasks_done")
                        : statType === 'in_progress'
                            ? t("statistics.tasks_in_progress")
                            : t("statistics.tasks_to_do")}

                </Typography>
                {loading ? (
                    <Box sx={{display:"flex", justifyContent:"center"}}>
                        <TinyCustomFallback />
                    </Box>
                ) : (
                    <>
                        {taskGroups.length === 0 ? (
                            <Typography>No tasks found.</Typography>
                        ) : (
                            taskGroups.map((group, idx) => (
                                <Box key={idx} sx={{ mb: 4 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 1, borderBottom: '1px solid #ccc' }}>
                                        {group.projectName}
                                    </Typography>
                                    {group.tasks.map((task, tIdx) => (
                                        <TaskCard
                                            key={tIdx}
                                            task={task}
                                            workspaceWorkers={workspaceWorkers}
                                            myUserTimezone={myUserTimezone}
                                            taskLabels={taskLabels}
                                        />
                                    ))}
                                </Box>
                            ))
                        )}
                    </>
                )}
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button variant="contained" onClick={handleClose}>Close</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default TaskStatisticsModal;
